import { Grid, Box } from "@mui/material";
import React, { useMemo, useRef } from "react";
import { styles } from "./create-app-page-adapters-list.styles";
import { StriimTypography } from "@striim/striim-ui-v2";
import { recentlyUsedAdaptersService } from "../../../services";
import AdapterItem from "./adapter-item/adapter-item";
import { CreateAppPageAdapterData } from "../../../create-app-page-utils";

interface AdaptersListProps {
    adapters: CreateAppPageAdapterData[];
    handleSelection: Function;
    type: String;
    selectedView: "list" | "grid";
    isAscendingSort: boolean;
    filters: {
        id: string;
        category: string;
    };
    hideRecentlyUsed?: boolean;
}

const CreateAppPageAdaptersList: React.FC<AdaptersListProps> = ({
    adapters,
    handleSelection,
    type,
    selectedView,
    isAscendingSort,
    filters,
    hideRecentlyUsed = false
}) => {
    const scrollableRef = useRef<HTMLDivElement>(undefined);
    const searchInput = filters.id;

    const recentlyUsedAdapters = useMemo(() => {
        let recentlyUsedAdapters: string[] = [];

        if (type.toUpperCase() === "SOURCE") {
            recentlyUsedAdapters = recentlyUsedAdaptersService.getSources();
        } else {
            recentlyUsedAdapters = recentlyUsedAdaptersService.getTargets();
        }
        return adapters
            .filter(adapter => recentlyUsedAdapters.includes(adapter.value))
            .sort((a, b) => recentlyUsedAdapters.indexOf(a.value) - recentlyUsedAdapters.indexOf(b.value));
    }, [type]);

    /**
     * First, split the arrays as disabled and enabled.
     * Then, sort the subarrays
     * Then, merge both the arrays.
     */
    const sortedAdapters = useMemo(() => {
        let enabledAdapters = adapters.filter(a => !!a.isEnabled);
        enabledAdapters.sort((a, b) => (isAscendingSort ? a.id.localeCompare(b.id) : b.id.localeCompare(a.id)));
        let disabledAdapters = adapters.filter(a => !a.isEnabled);
        disabledAdapters.sort((a, b) => (isAscendingSort ? a.id.localeCompare(b.id) : b.id.localeCompare(a.id)));
        return [].concat(enabledAdapters, disabledAdapters);
    }, [adapters, isAscendingSort]);

    return (
        <Grid sx={hideRecentlyUsed ? { ...styles.gridContainer, gridTemplateColumns: "1fr" } : styles.gridContainer}>
            <Grid sx={styles.scrollableContainer} ref={scrollableRef} data-test-id="all-adapters-container">
                {sortedAdapters.length === 0 ? (
                    <Box sx={{ textAlign: "center", mt: 4 }}>
                        <StriimTypography variant="body3" color="primary.900">
                            No components matching{" "}
                            <Box component="span" sx={{ wordBreak: "break-word" }}>
                                “{searchInput}”
                            </Box>
                        </StriimTypography>
                        <br />
                        <StriimTypography variant="body4" color="greyscale.700">
                            Try checking for typos or search using another keyword
                        </StriimTypography>
                    </Box>
                ) : (
                    <Grid
                        data-test-id={`supported-adapter-list-${selectedView}-display`}
                        sx={selectedView === "list" ? styles.itemListView : styles.itemGridView}
                    >
                        {sortedAdapters.map((adapter, i) => (
                            <AdapterItem
                                key={adapter.id}
                                adapter={adapter}
                                handleSelection={handleSelection}
                                type={type}
                                selectedView={selectedView}
                                filters={filters}
                                scrollableRef={scrollableRef}
                                recentlyUsedAdapterView={false}
                                initialInView={i < 15}
                            />
                        ))}
                    </Grid>
                )}
            </Grid>

            {!hideRecentlyUsed && (
                <Grid container flexDirection={"column"} gap={2} p={3} sx={styles.recentlyUsedContainer}>
                    <StriimTypography variant="caption2" sx={styles.recentlyUsedText} color="greyscale.700">
                        Recently Used
                    </StriimTypography>
                    {recentlyUsedAdapters.map(adapter => {
                        return (
                            <AdapterItem
                                key={adapter.id}
                                adapter={adapter}
                                handleSelection={handleSelection}
                                type={type}
                                selectedView={selectedView}
                                filters={filters}
                                scrollableRef={null}
                                initialInView={true}
                                recentlyUsedAdapterView
                            />
                        );
                    })}
                </Grid>
            )}
        </Grid>
    );
};

export default CreateAppPageAdaptersList;
